import { useTrackMutation } from '@mote/api'
import { TRACK_EVENT_NAMES } from '../../constants'

export const moteRecorderErrorMap = {
  NOT_LOGGED_IN: 'NOT_LOGGED_IN',
  ASK_FOR_MICROPHONE_PERMISSION: 'ASK_FOR_MICROPHONE_PERMISSION',
  FIX_MICROPHONE_PERMISSION: 'FIX_MICROPHONE_PERMISSION',
  STREAM_UNAVAILABLE_ERROR: 'STREAM_UNAVAILABLE_ERROR',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  CHUNKS_ERROR_THRESHOLD_ERROR: 'CHUNKS_ERROR_THRESHOLD_ERROR',
  MOTE_ID_UNDEFINED_ERROR: 'MOTE_ID_UNDEFINED_ERROR',
  POLLING_THRESHOLD_EXCEEDED: 'POLLING_THRESHOLD_EXCEEDED',
  FORCE_UPGRADE: 'FORCE_UPGRADE'
} as const

export const moteRecorderErrorMessages = {
  [moteRecorderErrorMap.NOT_LOGGED_IN]: {
    title: 'Login required',
    description: 'Please log in to continue'
  },
  [moteRecorderErrorMap.ASK_FOR_MICROPHONE_PERMISSION]: {
    title: 'Microphone access required',
    description:
      'Chrome is requesting microphone access on behalf of Mote. Click the "Allow" button to grant access'
  },
  [moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION]: {
    title: 'Microphone is blocked',
    description:
      "It looks like your microphone is blocked. You'll need to fix this in Chrome's settings. Search 'microphone blocked' on our Help site for more information"
  },
  [moteRecorderErrorMap.STREAM_UNAVAILABLE_ERROR]: {
    title: 'Microphone is not available',
    description: 'Please check your microphone and try again'
  },
  [moteRecorderErrorMap.QUOTA_EXCEEDED]: {
    title: 'Quota exceeded',
    description: ''
  },
  [moteRecorderErrorMap.UNKNOWN_ERROR]: {
    title: 'Something went wrong',
    description:
      'Whoops - something went wrong. Please try again, or search our FAQ pages for troubleshooting suggestions.'
  },
  [moteRecorderErrorMap.NETWORK_ERROR]: {
    title: 'Network issues',
    description:
      'Looks like you are not connected to the internet. Please make sure you have internet access and try again.'
  },
  [moteRecorderErrorMap.CHUNKS_ERROR_THRESHOLD_ERROR]: {
    title: 'Chunks failure threshold passed',
    description:
      'There was an error recording your mote. Please try again, or search our FAQ pages for troubleshooting suggestions'
  },
  [moteRecorderErrorMap.MOTE_ID_UNDEFINED_ERROR]: {
    title: 'Mote registration failed',
    description:
      'We were unable to complete the recording. Please check your internet connection and try again or kindly contact support'
  },
  [moteRecorderErrorMap.POLLING_THRESHOLD_EXCEEDED]: {
    title: 'Polling threshold exceeded',
    description:
      'Something went wrong with your recording. Please try again or kindly contact support'
  },
  [moteRecorderErrorMap.FORCE_UPGRADE]: {
    title: 'Extension is outdated',
    description:
      'It looks like you are using an old and unsupported version of Mote extension. Please update your extension in order to record a mote'
  }
} as const

export type MoteRecorderError = keyof typeof moteRecorderErrorMap

export function useMoteRecorderErrorTrack() {
  const track = useTrackMutation()

  return (error: MoteRecorderError, payload: object = {}) => {
    const errorPayload = moteRecorderErrorMessages[error]
    track.mutate!({
      variables: {
        event: TRACK_EVENT_NAMES.error.occurred,
        recordingEngine: 'v3',
        reason: errorPayload.title,
        display: errorPayload.description,
        errorCode: error,
        ...payload
      }
    })
  }
}
